import Loading from 'common/components/loading/loading';
import { NumberTile, TileData } from 'features/tiles/numberTile';
import Tile from 'features/tiles/tile';
import { FC } from 'react';
import {
  useGetTotalAdCostsQuery,
  useGetTotalRevenueQuery,
  useGetCountRevenueQuery,
  CountResponse
} from 'app/apis/queryApi';
import { useSelector } from 'react-redux';
import { RootState } from 'app/store';
import moment from 'moment';
import { getDateRangeAsObjects } from 'features/date-range-selection/dateRangeSlice';
import { selectCurrencyCode } from 'features/settings/settingsSlice';

const ROIMetricTile: FC = () => {
  const dateRange = useSelector(getDateRangeAsObjects);

  const { siteId } = useSelector((state: RootState) => state.selectedSite);

  const selectedCurrencyCode = useSelector(selectCurrencyCode);

  const {
    data: adCostData,
    isLoading: adCostIsLoading,
    isFetching: adCostIsFetching
  } = useGetTotalAdCostsQuery({
    siteId,
    startDate: moment(dateRange.startDate).format('DD/MM/YYYY'),
    endDate: moment(dateRange.endDate).format('DD/MM/YYYY'),
    currencyCode: selectedCurrencyCode
  });

  const {
    data: revenueData,
    isLoading: revenueIsLoading,
    isFetching: revenueIsFetching
  } = useGetTotalRevenueQuery({
    siteId,
    startDate: moment(dateRange.startDate).format('DD/MM/YYYY'),
    endDate: moment(dateRange.endDate).format('DD/MM/YYYY'),
    currencyCode: selectedCurrencyCode
  });

  const {
    data: salesData,
    isLoading: salesIsLoading,
    isFetching: salesIsFetching
  } = useGetCountRevenueQuery({
    siteId,
    startDate: moment(dateRange.startDate).format('DD/MM/YYYY'),
    endDate: moment(dateRange.endDate).format('DD/MM/YYYY')
  });

  const sumTileDataForDateRange = (tileData: CountResponse): number => {
    return Object.values(tileData).reduce((a, b) => a + b, 0);
  };

  const getDataForTile = (
    tileTitle: string,
    data: CountResponse[],
    locales: string,
    formatOptions: Intl.NumberFormatOptions
  ): TileData => {
    const totalRevenue = sumTileDataForDateRange(data[0]);
    const totalCost = sumTileDataForDateRange(data[1]);
    const roas =
      totalRevenue > 0 && totalCost > 0 ? (totalRevenue / totalCost) * 100 : 0;
    return {
      label: tileTitle,
      count:
        sumTileDataForDateRange(data[0]) - sumTileDataForDateRange(data[1]),
      metric: `${roas.toLocaleString(locales, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}% ROAS`,
      format: Intl.NumberFormat(locales, formatOptions)
    };
  };
  return (
    <>
      <Tile className="w-full">
        {!revenueIsLoading &&
        !revenueIsFetching &&
        revenueData != null &&
        !adCostIsLoading &&
        !adCostIsFetching &&
        adCostData != null &&
        !salesIsLoading &&
        !salesIsFetching &&
        salesData != null ? (
          <NumberTile
            tileData={{
              ...getDataForTile('ROI', [revenueData, adCostData], 'en-GB', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }),
              currencyCode: selectedCurrencyCode
            }}
          />
        ) : (
          <Loading />
        )}
      </Tile>
    </>
  );
};

export default ROIMetricTile;
